import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useClientStore } from '@core/client/useClientStore';
import useFeatureView from '@/hooks/useFeatureView';
import { useJobDetailData } from '@/modules/jobs/hooks/useJobDetailData';
import {
  getJobDetailView,
  type JobDetailTabKey,
} from '@/modules/jobs/views/jobDetail.view';
import Breadcrumbs from '@/components/Breadcrumbs';
import JobEditModal from '@/components/modals/JobEditModal';
import Tabs from '@/components/Tabs';
import {
  JobHeader,
  JobDetails,
  JobActivity,
  JobTimesheets,
  JobApplications,
} from '@/modules/jobs/components';
import RateSection from '@/modules/jobs/components/jobDrawer/rateSection/RateSection';
import AstuteRateSyncSection from '@/modules/jobs/components/jobDrawer/rateSection/AstuteRateSyncSection';

const ALL_JOB_TABS: { key: JobDetailTabKey; label: string }[] = [
  { key: 'details', label: 'Details' },
  { key: 'activity', label: 'Activity' },
  { key: 'applications', label: 'Applications' },
  { key: 'timesheets', label: 'Timesheets' },
];

const JobPage = () => {
  const { id } = useParams<{ id: string }>();
  const view = useFeatureView(getJobDetailView);
  const { getClientById } = useClientStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const {
    job,
    setJob,
    isLoading,
    error,
    availableWorkers,
    isLoadingAvailableWorkers,
    refreshJob,
  } = useJobDetailData(view, id);

  const visibleTabs = useMemo(
    () => ALL_JOB_TABS.filter((tab) => view.tabs.includes(tab.key)),
    [view.tabs],
  );

  const [selectedTab, setSelectedTab] = useState<JobDetailTabKey>(() => {
    const tabParam = searchParams.get('tab') as JobDetailTabKey | null;
    if (tabParam && view.tabs.includes(tabParam)) {
      return tabParam;
    }
    return view.tabs[0] ?? 'details';
  });

  useEffect(() => {
    const tabParam = searchParams.get('tab') as JobDetailTabKey | null;
    if (tabParam && view.tabs.includes(tabParam) && tabParam !== selectedTab) {
      setSelectedTab(tabParam);
    }
  }, [searchParams, view.tabs, selectedTab]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-600">{error || 'Job not found'}</div>
      </div>
    );
  }

  const client = getClientById(job.clientId);

  return (
    <div className="p-4">
      <Breadcrumbs
        items={[
          { label: 'Jobs', to: '/jobs' },
          { label: job.title || 'Job Details' },
        ]}
        className="mb-4"
      />
      <div className="mb-4">
        <JobHeader
          title={job.title || 'Job'}
          status={job.itemStatus || undefined}
          clientName={
            view.actions.showClientName ? (client?.name ?? '') : undefined
          }
          clientId={view.actions.showClientName ? job.clientId : undefined}
          clientLogoUrl={
            view.actions.showClientName ? (client?.logoUrl ?? '') : undefined
          }
        />
      </div>
      <Tabs
        tabs={visibleTabs}
        selectedTabKey={selectedTab}
        onTabChange={(key) => {
          const tabKey = key as JobDetailTabKey;
          setSelectedTab(tabKey);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('tab', tabKey);
            next.delete('editTimesheet');
            return next;
          });
        }}
      >
        {selectedTab === 'details' && (
          <div className="space-y-6">
            <JobDetails
              job={job}
              onWorkerAssigned={refreshJob}
              onWorkerUnassigned={refreshJob}
              onEdit={() => setIsEditModalOpen(true)}
              onCancelSuccess={() => {
                void refreshJob();
              }}
              showWorkerLink={view.actions.showWorkerLink}
              canAssignWorker={view.actions.assignWorker}
              canEditJob={view.actions.editJob}
              canEditFilledJob={view.actions.editFilledJob}
              showAssignedCandidateEmail={
                view.actions.showAssignedCandidateEmail
              }
              showAssignedCandidatePhone={
                view.actions.showAssignedCandidatePhone
              }
            />
            {view.actions.showRateSection && (
              <>
                <RateSection job={job} layout="page" onJobUpdated={setJob} />
                <AstuteRateSyncSection job={job} onJobUpdated={setJob} />
              </>
            )}
          </div>
        )}
        {selectedTab === 'activity' && (
          <JobActivity
            job={job}
            eligibleCandidateCount={availableWorkers.length}
            isLoadingEligibleCandidates={isLoadingAvailableWorkers}
          />
        )}
        {selectedTab === 'applications' && (
          <JobApplications jobId={job.id} onJobUpdated={setJob} />
        )}
        {selectedTab === 'timesheets' && <JobTimesheets job={job} />}
      </Tabs>
      {view.actions.editJob && (
        <JobEditModal
          canEditSite={view.actions.editSite}
          isModalOpen={isEditModalOpen}
          setIsModalOpen={setIsEditModalOpen}
          job={job}
          onSuccess={() => {
            void refreshJob();
          }}
        />
      )}
    </div>
  );
};

export default JobPage;
